import { ReactComponent as PlayerPause } from '@assets/components/button/icPlayer-pause.svg';
import { ReactComponent as PlayerPlay } from '@assets/components/button/icPlayer-play.svg';
import { IcCalendar } from '@assets/icons/24x24';
import { IcShare } from '@assets/icons/32x32';
import { Box, createListCollection, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import SensorRawExport from '@components/replay/export/SensorRawExport';
import VideoExport from '@components/replay/export/VideoExport';
import ReplayDatePopupForm from '@components/replay/ReplayDatePopupForm';
import { useReplayTimelineContext } from '@components/replay/ReplayTimelineContext';
import { CustomSelect } from '@components/ui/select/Select';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole } from '@redux/authent/authent.selectors';
import { closePopupByMapIdAndType, updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { selectReplaySpeed, selectReplayUuid } from '@redux/replay/replay.selectors';
import { useUpdateStreamMutation } from '@services/replay/replay.api';
import { EPSILON } from '@utils/common.utils';
import { findPopupByType, isOpenedPopupByType } from '@utils/popup.utils';
import { getNewRangeFromTimeScale } from '@utils/replay/replay.utils';
import { useRef } from 'react';
import { FormattedDate, FormattedTime, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { PopupTypeEnum } from '@/types/popup.types';
import { ReplayTimeScaleEnum } from '@/types/replay/replay.types';

export default function TimelineControls() {
  const { formatMessage } = useIntl();
  const hasRoleExportVideo = useAppSelector((state) => hasRole(state, RoleEnum.EXPORT_VIDEO));
  const hasRoleExportSensorRaw = useAppSelector((state) => hasRole(state, RoleEnum.ADMIN));
  const timelineRef = useRef<HTMLDivElement>(null);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const closePopup = useWithDispatch(closePopupByMapIdAndType);
  const { mapId, range, setDateRange, timeScale, setTimeScale, currentReplayTime, backwardForwardTooltip } =
    useReplayTimelineContext();
  const popups = useAppSelector((state) => selectPopupsByMapId(state, mapId));

  const replaySpeed = useAppSelector(selectReplaySpeed);
  const replayUuid = useAppSelector(selectReplayUuid);
  const isPlaying = !!replaySpeed && Math.abs(replaySpeed) > EPSILON;
  const [updateStream] = useUpdateStreamMutation();

  const isReplayDatesOpened = isOpenedPopupByType(popups, PopupTypeEnum.REPLAY);
  const isVideoExportOpened = isOpenedPopupByType(popups, PopupTypeEnum.VIDEO_EXPORT);
  const isSensorRawExportOpened = isOpenedPopupByType(popups, PopupTypeEnum.SENSOR_RAW_EXPORT);

  const replayPopupPosition = findPopupByType(popups, PopupTypeEnum.REPLAY)?.position;
  const videoExportPopupPosition = findPopupByType(popups, PopupTypeEnum.VIDEO_EXPORT)?.position;
  const sensorRawPopupPosition = findPopupByType(popups, PopupTypeEnum.SENSOR_RAW_EXPORT)?.position;

  const handleCalendar = (position: { x: number; y: number }) => {
    if (isReplayDatesOpened) {
      closePopup({ mapId, type: PopupTypeEnum.REPLAY });
    } else {
      updatePopup({
        mapId: mapId,
        popup: {
          type: PopupTypeEnum.REPLAY,
          position,
          data: null,
        },
      });
    }
  };

  const handleSelectTimeScale = (value: string) => {
    const newTimeScale = value as ReplayTimeScaleEnum;
    setTimeScale(newTimeScale);
    const { from, to } = getNewRangeFromTimeScale(newTimeScale, range.from, currentReplayTime);
    setDateRange(from, to);
  };

  const handleVideoExport = (position: { x: number; y: number }) => {
    if (isVideoExportOpened) {
      closePopup({ mapId, type: PopupTypeEnum.VIDEO_EXPORT });
    } else {
      updatePopup({
        mapId: mapId,
        popup: {
          type: PopupTypeEnum.VIDEO_EXPORT,
          position,
          data: null,
        },
      });
    }
  };

  const handleSensorRawExport = (position: { x: number; y: number }) => {
    if (isSensorRawExportOpened) {
      closePopup({ mapId, type: PopupTypeEnum.SENSOR_RAW_EXPORT });
    } else {
      updatePopup({
        mapId: mapId,
        popup: {
          type: PopupTypeEnum.SENSOR_RAW_EXPORT,
          position,
          data: null,
        },
      });
    }
  };

  const handlePlayStop = () => {
    if (replayUuid) {
      updateStream({ uuid: replayUuid, command: { speed: isPlaying ? 0 : 1 } });
    }
  };

  return (
    <HStack
      ref={timelineRef}
      alignItems="center"
      position="relative"
      backgroundColor="neutral.black"
      color="neutral.white"
      height="62px"
      gap={0}
    >
      {isReplayDatesOpened && replayPopupPosition && <ReplayDatePopupForm position={replayPopupPosition} />}
      {isVideoExportOpened && videoExportPopupPosition && <VideoExport position={videoExportPopupPosition} />}
      {isSensorRawExportOpened && sensorRawPopupPosition && <SensorRawExport position={sensorRawPopupPosition} />}
      <IconButton
        marginX={3}
        marginY="14px"
        aria-label="choose dates"
        variant="calendar"
        data-active={isReplayDatesOpened}
        onClick={(e) => handleCalendar({ x: e.clientX, y: e.clientY })}
      >
        <Icon as={IcCalendar} boxSize="24px" color="neutral.300" />
      </IconButton>
      <CustomSelect
        variant="darker"
        width="205px"
        size="sm"
        textStyle="md"
        value={timeScale}
        collection={createListCollection({
          items: Object.values(ReplayTimeScaleEnum).map((value) => ({
            label: formatMessage({ id: `replay.timeScale.${value}` }),
            value: value as string,
          })),
        })}
        onChange={(value) => value && handleSelectTimeScale(value)}
      />
      <Box marginLeft="auto" marginRight="102px">
        <Tooltip
          open={backwardForwardTooltip.isDisplayed}
          content={backwardForwardTooltip.label}
          positioning={{
            placement: 'top',
          }}
          closeDelay={300}
        >
          <IconButton
            aria-label={formatMessage({ id: `replay.timeline.actions.${isPlaying ? 'stop' : 'play'}` })}
            variant="smallPlayer"
            disabled={!replayUuid}
            onClick={handlePlayStop}
          >
            <Icon as={isPlaying ? PlayerPause : PlayerPlay} boxSize="32px" color="neutral.300" />
          </IconButton>
        </Tooltip>
      </Box>
      <VStack
        backgroundColor="neutral.800"
        width="151px"
        height="48px"
        marginRight="auto"
        top={0}
        alignItems="center"
        justifyContent="center"
        gap={0}
      >
        <Text>
          <FormattedDate value={currentReplayTime} day="numeric" month="numeric" year="numeric" />
        </Text>
        <Text as="b">
          <FormattedTime value={currentReplayTime} hour="numeric" minute="numeric" second="numeric" />
        </Text>
      </VStack>
      {hasRoleExportVideo && (
        <IconButton
          aria-label={formatMessage({ id: 'videoExport.title' })}
          variant="smallPlayer"
          justifySelf="end"
          marginRight="24px"
          title={formatMessage({ id: 'videoExport.title' })}
          data-active={isVideoExportOpened}
          onClick={(e) => handleVideoExport({ x: e.clientX, y: e.clientY })}
        >
          <Icon as={IcShare} boxSize="32px" color="neutral.300" />
        </IconButton>
      )}
      {hasRoleExportSensorRaw && (
        <IconButton
          aria-label={formatMessage({ id: 'sensorRawExport.title' })}
          variant="smallPlayer"
          justifySelf="end"
          marginRight="24px"
          title={formatMessage({ id: 'sensorRawExport.title' })}
          data-active={isSensorRawExportOpened}
          onClick={(e) => handleSensorRawExport({ x: e.clientX, y: e.clientY })}
        >
          <Icon as={IcShare} boxSize="32px" color="neutral.300" />
        </IconButton>
      )}
    </HStack>
  );
}
